/**
 * KAN-897: вкладки "Мои лиды" в приложении менеджера: "В работе" (исход open,
 * по умолчанию) и "Отработано" (won + lost, свежий исход сверху).
 * Вкладка живёт в адресе: ?tab=worked, без параметра "В работе".
 * KAN-915: "Следующая связь" (?tab=next): открытые лиды со связью позже текущего
 * момента, ближайшая сверху. Делит список сервер (?next_contact=future|due,
 * next_contact_at строки = столбец "Следующая связь" CRM): "В работе" получает
 * только лидов без связи или с наступившим сроком.
 */
import { MOBILE_LEADS_PAGE_SIZE, sortLeadsByActivity } from '@/lib/mobileClient';
import { splitNewMessages } from '@/lib/newMessage';
import { WORK_FAILURE_LABEL, WORK_SUCCESS_LABEL } from '@/lib/workActions';
import type { Client, ClientFilters } from '@/types/client';

export type MobileLeadsTab = 'work' | 'next' | 'worked';

export const MOBILE_LEADS_TABS: readonly MobileLeadsTab[] = ['work', 'next', 'worked'];

export const MOBILE_LEADS_TAB_LABELS: Record<MobileLeadsTab, string> = {
  work: 'В работе',
  next: 'Следующая связь',
  worked: 'Отработано',
};

export const MOBILE_LEADS_TAB_PARAM = 'tab';

export function parseMobileLeadsTab(value: string | null): MobileLeadsTab {
  return value === 'worked' || value === 'next' ? value : 'work';
}

/** Параметры списка вкладки (clientsApi.list). */
export function mobileLeadsFilters(tab: MobileLeadsTab, managerId: string, search: string): ClientFilters {
  const base: ClientFilters = { manager: managerId, search: search || undefined, page_size: MOBILE_LEADS_PAGE_SIZE };
  if (tab === 'worked') return { ...base, outcome: 'worked', ordering: '-outcome_at' };
  if (tab === 'next') return { ...base, outcome: 'open', next_contact: 'future', ordering: 'next_contact_at' };
  return { ...base, outcome: 'open', next_contact: 'due' };
}

export const MOBILE_LEADS_QUERY_ROOT = ['clients', 'mobile-leads'] as const;

export function mobileLeadsQueryKey(managerId: string | null, tab: MobileLeadsTab, search: string) {
  return [...MOBILE_LEADS_QUERY_ROOT, managerId, tab, search] as const;
}

const time = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : 0);

const byNextContact = (a: Client, b: Client) => time(a.next_contact_at) - time(b.next_contact_at);

/**
 * KAN-915: "В работе": лиды с наступившим сроком связи сверху (самый давний срок
 * первым: пора звонить), остальные по свежей активности. Сервер отдаёт сюда только
 * лидов без связи или с наступившим сроком, поэтому дата в строке = срок прошёл.
 * KAN-918: выше всех лиды с новым сообщением клиента (свежее сверху).
 */
function sortWorkLeads(clients: readonly Client[]): Client[] {
  const { fresh, rest: others } = splitNewMessages(clients);
  const due = others.filter((client) => !!client.next_contact_at).sort(byNextContact);
  const rest = sortLeadsByActivity(others.filter((client) => !client.next_contact_at));
  return [...fresh, ...due, ...rest];
}

/** Порядок строк: "В работе" (sortWorkLeads), "Следующая связь" по ближайшей, "Отработано" по свежему исходу. */
export function sortMobileLeads(tab: MobileLeadsTab, clients: readonly Client[]): Client[] {
  if (tab === 'work') return sortWorkLeads(clients);
  if (tab === 'next') return [...clients].sort(byNextContact);
  return [...clients].sort((a, b) => time(b.outcome_at) - time(a.outcome_at));
}

// Ссылки стабильны: фильтр теплоты пересчитывается только при смене вкладки.
const workLevel = (client: Client) => client.lead_temperature;
const workedLevel = (client: Client) => client.lead_temperature_stored;

/** Теплота для чипов: у закрытого лида lead_temperature пустая, берётся сохранённая. */
export function mobileLeadsLevelOf(tab: MobileLeadsTab): (client: Client) => string | undefined {
  return tab === 'worked' ? workedLevel : workLevel;
}

/** Бейдж исхода отработанного лида; open: null. */
export function mobileLeadOutcomeLabel(client: Pick<Client, 'outcome'>): string | null {
  if (client.outcome === 'won') return WORK_SUCCESS_LABEL;
  if (client.outcome === 'lost') return WORK_FAILURE_LABEL;
  return null;
}
